import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import '../types/channelTalk.d';

const PLUGIN_KEY = process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY || '';

// 広告自動化マンの「スタッフを呼ぶ」ボタン用。既存の初期化ロジックは変更しない。
// プラグインキー未設定時は false を返す（呼び出し側はボタン自体を非表示にする）。
export const isChannelTalkAvailable = (): boolean => !!PLUGIN_KEY;

// 既存のチャンネルトーク・ランチャーは消さず、メッセンジャーだけを開く。
// プラグインキー未設定・SDK未読み込み時も例外を投げない no-op。
//
// draft があれば新しいチャットを開き、入力欄に下書きとして入れる（openChat）。
// 入力欄に入るだけで自動送信はされない（チャネルトーク公式: "it appears in the input field"）。
// 送るかどうかは利用者が決める。draft が無ければ今までどおりメッセンジャーを開き、
// 既存の会話一覧にそのまま行けるようにする。
export const openChannelTalkMessenger = (draft?: string): void => {
  if (!PLUGIN_KEY) return;
  try {
    const text = draft?.trim();
    if (text) {
      window.ChannelIO?.('openChat', undefined, text);
    } else {
      window.ChannelIO?.('showMessenger');
    }
  } catch {
    // SDK未初期化などは静かに諦める（会話UIを壊さないため）
  }
};

// 広告自動化マンのパネル用（FIXES.md F13）。
// チャンネルトークのランチャーは z-index が極端に高く(2147483000級)、パネルの
// z-[1050]では絶対に勝てない。z-indexで対抗するのではなく、パネルが開いている
// 間だけランチャー自体を隠す。恒久的に消してはいけない（会議方針で残す判断済み）。
// ChannelIO未初期化（プラグインキー未設定・SDK未読み込み）でも例外を投げない no-op。
export const hideChannelTalkLauncher = (): void => {
  try {
    window.ChannelIO?.('hideChannelButton');
  } catch {
    // SDK未初期化などは静かに諦める
  }
};

export const showChannelTalkLauncher = (): void => {
  try {
    window.ChannelIO?.('showChannelButton');
  } catch {
    // SDK未初期化などは静かに諦める
  }
};

/**
 * Channel Talk (channel.io) chat widget hook.
 * Loads the SDK, boots with pluginKey, and syncs logged-in user info.
 */
export const useChannelTalk = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const bootedRef = useRef(false);

  useEffect(() => {
    if (!PLUGIN_KEY) return;

    // Load Channel Talk SDK
    (function () {
      const w = window as any;
      if (w.ChannelIO) return;

      const ch = function (...args: any[]) {
        ch.c(args);
      } as any;
      ch.q = [] as any[];
      ch.c = function (args: any) {
        ch.q.push(args);
      };
      w.ChannelIO = ch;

      function loadScript() {
        if (w.ChannelIOInitialized) return;
        w.ChannelIOInitialized = true;
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
        const x = document.getElementsByTagName('script')[0];
        if (x.parentNode) {
          x.parentNode.insertBefore(s, x);
        }
      }

      if (document.readyState === 'complete') {
        loadScript();
      } else {
        w.addEventListener('DOMContentLoaded', loadScript);
        w.addEventListener('load', loadScript);
      }
    })();
  }, []);

  // Boot / update when user changes
  useEffect(() => {
    if (!PLUGIN_KEY || !window.ChannelIO) return;

    const bootSettings: any = {
      pluginKey: PLUGIN_KEY,
      language: 'ja',
    };

    if (user) {
      bootSettings.memberId = String(user.id);
      bootSettings.profile = {
        name: user.name,
        email: user.email,
        organizationId: user.organization_id,
        organizationName: user.organization?.name,
        role: user.role,
      };
    }

    // Re-boot to update user info
    if (bootedRef.current) {
      window.ChannelIO('shutdown');
    }
    window.ChannelIO('boot', bootSettings);
    bootedRef.current = true;
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (window.ChannelIO && bootedRef.current) {
        window.ChannelIO('shutdown');
        bootedRef.current = false;
      }
    };
  }, []);
};
